import { FC, useCallback } from "react";
import {
    Card3Icon,
    IntegrationsIcon,
    MailIcon,
    MembersIcon,
    PlansIcon,
    LockIcon,
    ReceiptIcon,
    TasksIcon,
} from "@icons";
import { useSettings } from "@components/settings/SettingsProvider";
import { useEntities } from "@hooks/useEntities";
import { EntityPaneKey, getEntityPaneKey } from "@components/settings/lib";
import { Command } from "@vendor/cmdk/cmdk";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import {
    EntitySettingsPages,
    UserSettingsPages,
    WorkspaceSettingsPages,
} from "../settings/constants";
import { useDocumentInboxSettingsLabel } from "../settings/WorkspaceReceipts/useCanShowBillInvoiceInbox";
import { SettingsCommandItem } from "./SettingsCommandItem";
import { useCommands } from "./useCommands";

export const WorkspaceSettingsCommandsList: FC = () => {
    const { open: openSettings } = useSettings();
    const { close: closeCommands } = useCommands();
    const documentInboxLabel = useDocumentInboxSettingsLabel();

    const handleSelected = useCallback(
        (
            path: Array<
                WorkspaceSettingsPages | UserSettingsPages | EntityPaneKey
            >,
        ) => {
            openSettings(path);
            closeCommands();
        },
        [openSettings, closeCommands],
    );

    const entities = useEntities({
        requireAdminAccess: true,
    });

    return (
        <Command.Group heading="Workspace settings">
            <SettingsCommandItem
                path={WorkspaceSettingsPages.MEMBERS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.MEMBERS])
                }
                keywords={["custom"]}
            >
                <MembersIcon /> Go to Members
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.TASK_GROUPS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.TASK_GROUPS])
                }
                keywords={["tasks", "task groups", "groups"]}
            >
                <TasksIcon /> Go to Task groups
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.INTEGRATIONS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.INTEGRATIONS])
                }
            >
                <IntegrationsIcon /> Go to Integrations
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.LOCKS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.LOCKS])
                }
            >
                <LockIcon /> Go to Locks
            </SettingsCommandItem>

            <SettingsCommandItem
                path={WorkspaceSettingsPages.DOCUMENT_INBOX}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.DOCUMENT_INBOX])
                }
                keywords={[
                    "receipts",
                    "document inbox",
                    "bills",
                    "invoices",
                    "forwarding",
                    "email",
                ]}
            >
                <ReceiptIcon /> Go to {documentInboxLabel.toLowerCase()}
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.SENDING_DOMAINS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.SENDING_DOMAINS])
                }
                keywords={["email", "smtp", "dns", "sender", "domain"]}
            >
                <MailIcon /> Go to Sending domains
            </SettingsCommandItem>

            <SettingsCommandItem
                path={WorkspaceSettingsPages.PLANS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.PLANS])
                }
            >
                <PlansIcon /> Go to Plans
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.BILLING}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.BILLING])
                }
            >
                <Card3Icon /> Go to Billing
            </SettingsCommandItem>
            {entities.map((entity, index) => {
                const key = getEntityPaneKey(
                    EntitySettingsPages.SETTINGS,
                    entity.id,
                );
                return (
                    <SettingsCommandItem
                        key={entity.id}
                        validPathOverride={key}
                        onSelected={() =>
                            handleSelected([
                                getEntityPaneKey(
                                    EntitySettingsPages.SETTINGS,
                                    entity.id,
                                ),
                            ])
                        }
                        keywords={["entity", entity.name ?? ""]}
                        shortcut={`g>${index + 1}`}
                    >
                        <EntityAvatar entity={entity} size="xs" aria-hidden />{" "}
                        {entity.name}
                    </SettingsCommandItem>
                );
            })}
        </Command.Group>
    );
};
